import { Button } from "@/components/generic/Button";
import {
  Integration,
  ProviderConfigurationMetadata,
} from "@/types/integrations";
import { configureOAuth } from "@/utils/integration-helpers";
import { Check, LoaderCircle } from "lucide-react";
import normalizeUrl from "normalize-url";
import React, { useState } from "react";

interface OAuthButtonProps {
  providerConfig: ProviderConfigurationMetadata;
  oauthResponseChannelId: string;
  url?: string;
  defaultText?: string;
  integration?: Integration;
  disabled?: boolean;
  verifiedConnection?: boolean;
  onOauth?: () => void;
  onSuccess?: (normalizedUrl?: string) => void;
  onFinished?: () => void;
  children?: React.ReactNode;
}

export const OAuthButton: React.FC<OAuthButtonProps> = ({
  providerConfig,
  oauthResponseChannelId,
  url,
  defaultText,
  verifiedConnection,
  integration,
  disabled = false,
  onOauth,
  onSuccess,
  onFinished,
  children,
}) => {
  const [isPending, setIsPending] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  if (!integration) {
    return null; // or handle the case where integration is not provided
  }
  const handleClick = async () => {
    if (isPending || disabled) return;

    onOauth?.();
    setIsPending(true);
    try {
      const oauthUrl = url
        ? normalizeUrl(url, {
            defaultProtocol: "https",
            forceHttps: true,
          })
        : undefined;

      // Safely construct the API URL to avoid double slashes and ensure valid URL
      const apiUrl = oauthUrl
        ? new URL("/api/data/v9.2", oauthUrl).toString()
        : undefined;

      await configureOAuth(
        providerConfig,
        integration,
        oauthResponseChannelId,
        apiUrl,
      );
      onSuccess?.(oauthUrl);
    } catch (err) {
      console.error("OAuth process failed:", err);
    } finally {
      onFinished?.();
      setIsPending(false);
      setTestingConnection(false);
    }
  };

  return (
    <Button
      intent="secondary"
      onClick={handleClick}
      isDisabled={isPending || testingConnection || disabled}
    >
      <div className="flex items-center gap-2">
        {isPending ? (
          <>
            <LoaderCircle className="size-6 animate-spin" />
            Venter på {integration.name}...
          </>
        ) : (
          (children ?? (
            <>
              {(isPending || testingConnection) && (
                <LoaderCircle className="size-6 animate-spin" />
              )}
              {!isPending && verifiedConnection && (
                <Check className="size-6 text-green-600 dark:text-green-400" />
              )}
              {testingConnection
                ? `Tester forbindelse til ${integration.name}`
                : isPending
                  ? `Venter på ${integration.name}...`
                  : verifiedConnection
                    ? `Forbundet med ${integration.name}`
                    : (defaultText ?? `Forbind med ${integration.name}`)}
            </>
          ))
        )}
      </div>
    </Button>
  );
};
